import * as React from "react";
import { InputProps } from "baseui/input";
import { useRifm } from "rifm";

import { Input } from "../Input";
import { NumberInput } from "./NumberInput";

// NOTE: We should probably be storing currency as an integer representation of cents

const CurrencyInput: React.FC<InputProps> = (props) => (
  <NumberInput {...props} customInput={TransformingInput} type="tel" />
);

interface Props {
  value?: string | number | undefined; // Updated the type of 'value' to 'string | number | undefined'
  onChange: (value: string | undefined) => void; // Updated the parameter type to 'string | undefined'
}
const TransformingInput = (props: Props) => {
  const rifm = useRifm({
    accept: numberAccept,
    format: formatCurrency,
    value: parseNumber(props.value),
    onChange: (value) => props.onChange(parseNumber(value)),
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    rifm.onChange(e);
  };

  return <Input {...props} value={rifm.value} onChange={handleChange} />;
};

// Currency formatting adapted from rifm documentation examples:

const formatCurrency = (string: string) => {
  const numberString = formatFloatingPointNumber(string, 2);
  return numberString && `$${numberString}`;
};

export const formatFloatingPointNumber = (value: string, digits: number) => {
  const parsed = parseNumber(value);
  const [head, tail] = parsed.split(".");
  // Avoid rounding errors at toLocaleString, as when user enters 1.239 and digits=2
  // we need to convert to 1.23, not 1.24
  const scaledTail = tail != null ? tail.slice(0, digits) : "";
  const number = Number.parseFloat(`${head}.${scaledTail}`);

  // Allow initial typing of negative numbers
  if (head === "-" || head === "--") {
    return "-";
  }

  if (Number.isNaN(number)) {
    return "";
  }

  const formatted = number.toLocaleString("en-AU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });

  if (parsed.includes(".")) {
    const [formattedHead] = formatted.split(".");
    // Skip zero at final digits position. i.e. 1.50 becomes 1.5
    // 1.0 should be allowed in order to enter 1.05.
    // Alternatively, we could always enforce min and max digits to be digits.
    const formattedTail =
      scaledTail !== "" && scaledTail[digits - 1] === "0"
        ? scaledTail.slice(0, -1)
        : scaledTail;
    return `${formattedHead}.${formattedTail}`;
  }
  return formatted;
};

const numberAccept = /[\d.-]+/g;

const parseNumber = (string: string) =>
  (`${string}`?.match?.(numberAccept) || []).join("");

export { CurrencyInput, formatCurrency };
